// @gs/feedback/client — TicketList : liste/triage (staff voit tout, sinon les siens).
// DS V2 : table sobre bg-white rounded-xl shadow-xs, chips subtils, point non-lu.
import { useMemo, useState } from 'react';
import { Archive, ArchiveRestore, Lock, Mail, MailCheck, MailOpen, Megaphone, Plus, Search } from 'lucide-react';
import { GsFacetFilter } from '@gs/ui';
import { useTranslation } from '@gs/i18n';
import {
  ARCHIVED_STATUS,
  FEEDBACK_PRIORITIES,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  nextLifecycleStatus,
  ticketKind,
  type TicketDto,
} from '../schemas';
import { ANNOUNCEMENT_CHIP, BTN_PRIMARY, BTN_SECONDARY, CHIP_BASE, COMPANY_CHIP, INPUT_BASE, ORIGIN_APP_CHIP, PRIORITY_CHIP, STATUS_CHIP, TYPE_CHIP } from './chipTokens';
import { formatDateTime } from './formatDateTime';
import { filterTickets, KIND_FILTERS, type KindFilter } from './filterTickets';
import { FEEDBACK_ORIGIN_APP_FILTER_KEYS, originAppLabel } from './originApp';
import { useMarkAllRead, useMarkRead, useMarkUnread, useSetTicketStatus } from './hooks';

export interface TicketListProps {
  tickets: TicketDto[];
  staff: boolean;
  onOpen: (ticketId: string) => void;
  onNew: () => void;
  /** Mig 293 appliquee (feat-feedback-les-nouveautes-se-publient) : filtre « Tout / Commentaires /
   *  Nouveautes » (F-FBK-20). Absent = serveur d avant, aucun filtre de nature. */
  announcements?: boolean;
  /** Bouton « Publier une nouveaute » (F-COQ-22) : le serveur dit si le lecteur peut publier. */
  canPublish?: boolean;
  onPublish?: () => void;
}

/** Libelle de chaque choix du filtre de nature (cles statiques, aucune cle dynamique). */
const KIND_FILTER_KEYS: Record<KindFilter, string> = {
  ALL: 'feedback:list.kindAll',
  FEEDBACK: 'feedback:list.kindFeedback',
  ANNOUNCEMENT: 'feedback:list.kindAnnouncement',
};

export function TicketList({ tickets, staff, onOpen, onNew, announcements = false, canPublish = false, onPublish }: TicketListProps) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  // Filtre de nature (F-FBK-20, prototype FeedbackBoard.tsx:160) : un seul choix a la fois.
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');
  // Facettes MULTI-VALEURS (CHORE-FBK-FILTERS-MULTI, norme Linear/GitHub) :
  // OU a l'interieur d'une facette, ET entre facettes ; vide = tous. Les
  // <select> natifs (menu OS hors DS + mono-valeur) sont remplaces par le
  // composant partage GsFacetFilter (@gs/ui).
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [originAppFilter, setOriginAppFilter] = useState<string[]>([]);
  // Filtre inbox « Non lus » (CHORE-FBK-UNREAD-INBOX, demande Marc) — pattern
  // boite courriel : isoler les tickets avec de l'activite non vue. Logique de
  // filtre = helper PUR filterTickets (teste sans DOM).
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Lu/non-lu manuel + tout marquer lu (CHORE-FBK-INBOX-V2, pattern Gmail).
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();
  const markAll = useMarkAllRead();
  // Archiver/Rouvrir par ligne (CHORE-FBK-LIFECYCLE, staff seulement) — un geste
  // depuis la liste, sans ouvrir le fil. Reutilise le PATCH triage (staff-only).
  const setStatus = useSetTicketStatus();

  const filtered = useMemo(
    () => filterTickets(tickets, {
      search, types: typeFilter, statuses: statusFilter, priorities: priorityFilter,
      originApps: originAppFilter, unreadOnly, kind: announcements ? kindFilter : 'ALL',
    }),
    [tickets, search, typeFilter, statusFilter, priorityFilter, originAppFilter, unreadOnly, announcements, kindFilter],
  );

  // Portee « tout marquer lu » = les non-lus ACTUELLEMENT filtres/affiches
  // (jamais toute la boite) ; confirmation legere au-dela de 10 (pattern Gmail).
  const unreadFilteredIds = useMemo(
    () => filtered.filter((tk) => tk.unread).map((tk) => tk.id),
    [filtered],
  );
  const markAllFiltered = () => {
    if (unreadFilteredIds.length === 0 || markAll.isPending) return;
    if (unreadFilteredIds.length > 10
      && !window.confirm(t('feedback:list.markAllReadConfirm', { count: unreadFilteredIds.length }))) return;
    markAll.mutate(unreadFilteredIds);
  };

  return (
    <div className="rounded-xl bg-white shadow-xs dark:bg-slate-900">
      {/* En-tete en DEUX rangees (CHORE-FBK-LIST-LAYOUT, norme Linear/GitHub) :
          (1) titre + action primaire ; (2) toolbar de filtres. Les deux partagent
          les gouttieres du TABLEAU (px-4 du conteneur = padding de la 1re/derniere
          colonne) — fini la rangee « plus large » aux wraps irreguliers : la
          toolbar wrappe en lignes completes, bord gauche constant. */}
      <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className="min-w-0 truncate text-field font-semibold text-slate-800 dark:text-slate-100">
            {staff ? t('feedback:list.title') : t('feedback:list.titleMine')}
          </h2>
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('feedback:list.subtitle')}
          </p>
        </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {/* « Publier une nouveaute » (F-COQ-22) : le personnel de l app, quand la 293 est la. */}
            {canPublish && onPublish && (
              <button type="button" className={`touch-manipulation ${BTN_SECONDARY} inline-flex shrink-0 items-center gap-1.5 disabled:opacity-45`} onClick={onPublish}>
                <Megaphone size={13} aria-hidden /> {t('feedback:publish.button')}
              </button>
            )}
            <button type="button" className={`touch-manipulation ${BTN_PRIMARY} inline-flex shrink-0 items-center gap-1.5 disabled:opacity-45`} onClick={onNew}>
              <Plus size={13} /> {t('feedback:list.new')}
            </button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* Filtre de nature (F-FBK-20) : « Tout / Commentaires / Nouveautes », un choix a la fois. */}
        {announcements && (
          <div role="group" aria-label={t('feedback:list.kindAria')}
            className="inline-flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
            {KIND_FILTERS.map((k) => (
              <button key={k} type="button" aria-pressed={kindFilter === k} onClick={() => setKindFilter(k)}
                className={[
                  'touch-manipulation rounded-md px-2 py-1 text-meta font-medium transition-colors',
                  kindFilter === k
                    ? 'bg-white text-slate-800 shadow-xs dark:bg-slate-900 dark:text-slate-100'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                ].join(' ')}>
                {t(KIND_FILTER_KEYS[k])}
              </button>
            ))}
          </div>
        )}
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
          <input className={`${INPUT_BASE} w-52 pl-7`} value={search}
            placeholder={t('feedback:list.searchPlaceholder')}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
        {/* Facettes multi-selection (GsFacetFilter — popover DS, compteur dans
            le libelle « Type · 2 ») : OU intra-facette, ET entre facettes,
            vide = tous. Tout reste combinable (recherche, « Non lus »). */}
        <GsFacetFilter
          facetLabel={t('feedback:list.facetOriginApp')}
          allLabel={t('feedback:list.filterAllApps')}
          options={FEEDBACK_ORIGIN_APP_FILTER_KEYS.map((v) => ({
            value: v,
            label: originAppLabel(t, v),
          }))}
          selected={originAppFilter}
          onChange={setOriginAppFilter}
          clearLabel={t('feedback:list.facetClear')}
        />
        <GsFacetFilter
          facetLabel={t('feedback:list.facetType')}
          allLabel={t('feedback:list.filterAllTypes')}
          options={FEEDBACK_TYPES.map((v) => ({ value: v, label: t(`feedback:type.${v}`) }))}
          selected={typeFilter}
          onChange={setTypeFilter}
          clearLabel={t('feedback:list.facetClear')}
        />
        <GsFacetFilter
          facetLabel={t('feedback:list.facetStatus')}
          allLabel={t('feedback:list.filterAllStatuses')}
          options={FEEDBACK_STATUSES.map((v) => ({ value: v, label: t(`feedback:status.${v}`) }))}
          selected={statusFilter}
          onChange={setStatusFilter}
          clearLabel={t('feedback:list.facetClear')}
        />
        <GsFacetFilter
          facetLabel={t('feedback:list.facetPriority')}
          allLabel={t('feedback:list.filterAllPriorities')}
          options={FEEDBACK_PRIORITIES.map((v) => ({ value: v, label: t(`feedback:priority.${v}`) }))}
          selected={priorityFilter}
          onChange={setPriorityFilter}
          clearLabel={t('feedback:list.facetClear')}
        />
        {/* Filtre binaire « Non lus » : toggle, meme cran que les selects de la
            rangee — actif = encre pleine (slate, brand reserve au wayfinding). */}
        <button type="button" onClick={() => setUnreadOnly((p) => !p)} aria-pressed={unreadOnly}
          className={[
            'touch-manipulation rounded-lg px-2 py-1.5 text-meta font-medium transition-colors disabled:opacity-45',
            unreadOnly
              ? BTN_PRIMARY
              : `${BTN_SECONDARY} px-2 py-1.5 text-meta`,
          ].join(' ')}>
          {t('feedback:list.filterUnread')}
        </button>
        {/* Tout marquer lu (CHORE-FBK-INBOX-V2) : visible seulement si des
            non-lus existent dans la vue courante (pattern cloche markAllRead —
            mecanique NON factorisee : autre table, autre semantique). */}
        {unreadFilteredIds.length > 0 && (
          <button type="button" onClick={markAllFiltered} disabled={markAll.isPending}
            className={`touch-manipulation ${BTN_SECONDARY} inline-flex items-center gap-1 px-2 py-1.5 text-meta disabled:opacity-45`}>
            <MailCheck size={13} aria-hidden /> {t('feedback:list.markAllRead')}
          </button>
        )}
        </div>
      </div>

      {/* UN SEUL gabarit de colonnes (CHORE-FBK-LIST-LAYOUT) : 1 en-tete = 1
          cellule, au pixel (memes px-4/px-2 que les <td>). TICKET = colonne
          flexible (w-full + max-w-0 : l'ellipsis fonctionne en table auto) ;
          les autres colonnes epousent leur contenu (whitespace-nowrap) ;
          l'action (enveloppe) a SA colonne sans titre visible (sr-only). */}
      {filtered.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:list.empty')}</p>
          <p className="mt-1 text-meta text-slate-400 dark:text-slate-500">{t('feedback:list.emptyHint')}</p>
        </div>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-100 text-chip uppercase tracking-wide text-slate-400 dark:border-slate-800">
              <th className="px-4 py-2 font-medium">{t('feedback:list.colTicket')}</th>
              {staff && <th className="px-2 py-2 font-medium">{t('feedback:list.colAuthor')}</th>}
              <th className="px-2 py-2 font-medium">{t('feedback:list.colActivity')}</th>
              <th className="px-2 py-2 font-medium">{t('feedback:list.colPriority')}</th>
              <th className="px-2 py-2 font-medium">{t('feedback:list.colStatus')}</th>
              <th className="px-4 py-2 font-medium">
                <span className="sr-only">{t('feedback:list.colActions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tk) => (
              <tr key={tk.id} onClick={() => onOpen(tk.id)}
                className="group cursor-pointer border-b border-slate-50 transition-colors hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/50">
                <td className="w-full max-w-0 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    {/* Langage visuel de la cloche (CHORE-FBK-UNREAD-INBOX) : point
                        brand = non-lu ; si des messages non vus sont comptes, le
                        point devient le badge compteur (meme style que le bouton
                        cloche). unreadCount peut etre 0 avec unread=true (ticket
                        frais, activite de triage) -> point simple. */}
                    {tk.unread && (tk.unreadCount > 0 ? (
                      <span aria-label={t('feedback:list.unreadAria', { count: tk.unreadCount })}
                        className="flex h-3.5 min-w-3.5 shrink-0 items-center justify-center rounded-full bg-brand-500 px-0.5 text-chip font-bold text-white">
                        {tk.unreadCount > 9 ? '9+' : tk.unreadCount}
                      </span>
                    ) : (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-brand-500" aria-label={t('feedback:list.unreadAria', { count: 1 })} />
                    ))}
                    <div className="min-w-0">
                      <div className={`truncate text-meta ${tk.unread ? 'font-bold' : 'font-medium'} text-slate-800 dark:text-slate-100`}>
                        {tk.title}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-chip text-slate-400">
                        <span className="font-mono">{tk.ticketNumber}</span>
                        {/* Une nouveaute porte sa nature (et « Ciblee ») a la place du type. */}
                        {ticketKind(tk) === 'ANNOUNCEMENT' ? (
                          <>
                            <span className={`${CHIP_BASE} ${ANNOUNCEMENT_CHIP} gap-1`}>
                              <Megaphone size={10} aria-hidden /> {t('feedback:kind.ANNOUNCEMENT')}
                            </span>
                            {tk.visibility === 'TARGETED' && (
                              <span className={`${CHIP_BASE} ${COMPANY_CHIP} gap-1`}>
                                <Lock size={10} aria-hidden /> {t('feedback:visibility.TARGETED')}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className={`${CHIP_BASE} ${TYPE_CHIP[tk.type]}`}>{t(`feedback:type.${tk.type}`)}</span>
                        )}
                        <span className={`${CHIP_BASE} ${ORIGIN_APP_CHIP}`}
                          title={t('feedback:originAppChipTitle', { app: originAppLabel(t, tk.originBcKey) })}>
                          {originAppLabel(t, tk.originBcKey)}
                        </span>
                        {/* Chip provenance (CHORE-FIX-B1 P2) : le serveur ne renseigne
                            companyName/Code QUE pour un lecteur corporate sur un ticket
                            d'une autre compagnie — le corporate sait D'OU il vient. */}
                        {(tk.companyName ?? tk.companyCode) && (
                          <span className={`${CHIP_BASE} ${COMPANY_CHIP}`}
                            title={t('feedback:companyOrigin', { name: tk.companyName ?? tk.companyCode })}>
                            {tk.companyName ?? tk.companyCode}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                {staff && (
                  <td className="whitespace-nowrap px-2 py-2.5 text-meta text-slate-500 dark:text-slate-400">{tk.createdByName}</td>
                )}
                <td className="whitespace-nowrap px-2 py-2.5 text-meta text-slate-500 dark:text-slate-400">
                  {formatDateTime(tk.lastActivityAt, i18n.language)}
                </td>
                <td className="whitespace-nowrap px-2 py-2.5">
                  <span className={`${CHIP_BASE} ${PRIORITY_CHIP[tk.priority]}`}>{t(`feedback:priority.${tk.priority}`)}</span>
                </td>
                <td className="whitespace-nowrap px-2 py-2.5">
                  <span className={`${CHIP_BASE} ${STATUS_CHIP[tk.status]}`}>{t(`feedback:status.${tk.status}`)}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right">
                  <div className="inline-flex items-center justify-end gap-0.5">
                    {/* Archiver/Rouvrir par ligne (CHORE-FBK-LIFECYCLE, staff seulement,
                        « idealement comme l'enveloppe ») : meme reveal au survol/focus.
                        ARCHIVED = sorti de la vue active ; rouvrir = retour « En cours »
                        (REOPEN_STATUS). stopPropagation : la row reste cliquable. */}
                    {staff && (
                      <button type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (setStatus.isPending) return;
                          setStatus.mutate({ ticketId: tk.id, status: nextLifecycleStatus(tk.status) });
                        }}
                        aria-label={tk.status === ARCHIVED_STATUS ? t('feedback:list.reopen') : t('feedback:list.archive')}
                        title={tk.status === ARCHIVED_STATUS ? t('feedback:list.reopen') : t('feedback:list.archive')}
                        className="touch-manipulation rounded-sm p-1 align-middle text-slate-400 transition-opacity hover:bg-slate-100 hover:text-slate-600 sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100 dark:hover:bg-slate-700 dark:hover:text-slate-300">
                        {tk.status === ARCHIVED_STATUS ? <ArchiveRestore size={13} aria-hidden /> : <Archive size={13} aria-hidden />}
                      </button>
                    )}
                    {/* Action lu/non-lu par ligne (CHORE-FBK-INBOX-V2, enveloppe Gmail) :
                        revelee au survol/focus (toujours visible < sm, pas de hover
                        tactile). ICONE = ETAT (norme Gmail, CHORE-FBK-LIFECYCLE) :
                        non-lu -> enveloppe PLEINE/FERMEE (Mail) ; lu -> enveloppe
                        OUVERTE (MailOpen). L'aria/title portent le VERBE d'ACTION
                        (« Marquer comme lu » sur un non-lu) — icone=etat, tooltip=action.
                        stopPropagation : la row reste cliquable (ouvre le fil).
                        Colonne dediee (CHORE-FBK-LIST-LAYOUT) : l'enveloppe ne flotte
                        plus sous l'en-tete STATUT. */}
                    <button type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (markRead.isPending || markUnread.isPending) return;
                        if (tk.unread) markRead.mutate(tk.id); else markUnread.mutate(tk.id);
                      }}
                      aria-label={tk.unread ? t('feedback:list.markRead') : t('feedback:list.markUnread')}
                      title={tk.unread ? t('feedback:list.markRead') : t('feedback:list.markUnread')}
                      className="touch-manipulation rounded-sm p-1 align-middle text-slate-400 transition-opacity hover:bg-slate-100 hover:text-slate-600 sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100 dark:hover:bg-slate-700 dark:hover:text-slate-300">
                      {tk.unread ? <Mail size={13} aria-hidden /> : <MailOpen size={13} aria-hidden />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
